import type { ModuleStudioRepository } from '../data/ModuleStudioRepository'
import {
  PROTOTYPE_RIGHTS_TERMS_VERSION,
  type Idea,
  type IdeaInput,
  type IdeaEstimate,
  type IdeaTransferInput,
} from '../data/contracts'
import ideaDemo from './ideaDemo.json'
const DEMO_IDEA_HTML = ideaDemo.html
const demoBrief =
  '# Prêt de matériel\n\n## Objectif\nSuivre les prêts de matériel fictif.\n\n## Objets\n- Matériel\n- Prêts\n\n## Écrans\n- Liste du matériel\n- Prêts en cours'
const fail = (code: string): never => {
  throw Object.assign(new Error(code), {
    response: { status: 400, data: { code } },
  })
}
/** Démonstration explicite, mémoire seule, aucune lecture de dépôt ou appel IA. */
export class IdeaFixture {
  private ideas = new Map<string, Idea>()
  private trees = new Map<
    string,
    {
      filesRead: number
      readme: string
      files: { path: string; bytes: number; truncated: boolean }[]
    }
  >()
  constructor(
    private host: Pick<
      ModuleStudioRepository,
      'saveDossier' | 'createPrototypeImport'
    >,
  ) {
    const now = new Date().toISOString()
    this.ideas.set('demo-idea-pret-materiel', {
      id: 'demo-idea-pret-materiel',
      status: 'prototyped',
      sourceKind: 'text',
      sourceUrl: null,
      ideaText: 'Gérer les prêts de matériel',
      locale: 'fr',
      brief: {
        text: demoBrief,
        json: {
          title: 'Prêt de matériel',
          goal: 'Suivre les prêts de matériel fictif.',
        },
      },
      prototype: {
        sha256: 'demo',
        bytes: DEMO_IDEA_HTML.length,
        screens: 2,
        generatedAt: now,
      },
      remarksText: null,
      phase: null,
      route: { route: 'demo', model: 'demo', provider: 'demo' },
      tokens: { prompt: 0, completion: 0 },
      transfer: null,
      createdAt: now,
      updatedAt: now,
    })
  }
  private read(id: string): Idea {
    return this.ideas.get(id) ?? fail('idea.not_found')
  }
  private write(id: string): Idea {
    const idea = this.read(id)
    if (idea.transfer) fail('idea.transferred')
    return idea
  }
  async listIdeas() {
    return Array.from(this.ideas.values())
      .sort(
        (a, b) =>
          Number(!!a.transfer) - Number(!!b.transfer) ||
          b.updatedAt.localeCompare(a.updatedAt),
      )
      .slice(0, 20)
  }
  async createIdea(input: IdeaInput) {
    if (
      input.ideaText.length > 4000 ||
      (input.sourceKind === 'text' && !input.ideaText.trim())
    )
      fail('idea.text_invalid')
    if (
      input.sourceKind === 'repository' &&
      !/^https:\/\/(github\.com|dev\.azure\.com|[a-z0-9-]+\.visualstudio\.com)\//.test(
        input.sourceUrl ?? '',
      )
    )
      fail('idea.repo_url_invalid')
    const now = new Date().toISOString()
    const id = `idea-${Date.now()}-${this.ideas.size}`
    const idea: Idea = {
      ...input,
      id,
      status: 'drafting',
      brief: null,
      prototype: null,
      phase: null,
      remarksText: null,
      route: { route: 'demo', model: 'demo', provider: 'demo' },
      tokens: { prompt: 0, completion: 0 },
      transfer: null,
      createdAt: now,
      updatedAt: now,
    }
    this.ideas.set(id, idea)
    return { ...idea }
  }
  async getIdea(id: string) {
    return { ...this.read(id) }
  }
  async updateIdea(id: string, input: IdeaInput) {
    const idea = this.write(id)
    if (
      input.sourceKind !== idea.sourceKind ||
      (input.sourceUrl ?? null) !== (idea.sourceUrl ?? null)
    )
      fail('idea.repo_url_invalid')
    Object.assign(idea, input, { updatedAt: new Date().toISOString() })
    return { ...idea }
  }
  async ideaEstimate(
    id: string,
    phase: 'brief' | 'prototype',
  ): Promise<IdeaEstimate> {
    const idea = this.read(id)
    return {
      phase,
      inputChars: idea.ideaText.length,
      estimatedPromptTokens: 100,
      outputMaxTokens: 2500,
      route: 'demo',
      provider: 'demo',
      model: 'demo',
      quota: null,
      estimatedChf: 0,
      rateKnown: true,
    }
  }
  async fetchIdeaRepository(id: string) {
    const idea = this.write(id)
    this.trees.set(id, {
      filesRead: 1,
      readme: '# Démonstration',
      files: [{ path: 'README.md', bytes: 20, truncated: false }],
    })
    idea.phase = {
      name: 'fetch',
      status: 'done',
      error: null,
      startedAt: new Date().toISOString(),
      finishedAt: new Date().toISOString(),
    }
    return { ...idea }
  }
  async getIdeaRepository(id: string) {
    return this.trees.get(id) ?? fail('idea.not_found')
  }
  async startBrief(id: string) {
    const idea = this.write(id)
    idea.brief = {
      text: demoBrief,
      json: {
        title: 'Prêt de matériel',
        goal: 'Suivre les prêts de matériel fictif.',
      },
    }
    idea.status = 'briefed'
    idea.phase = {
      name: 'brief',
      status: 'done',
      error: null,
      startedAt: new Date().toISOString(),
      finishedAt: new Date().toISOString(),
    }
    return { ...idea }
  }
  async acceptBrief(id: string, input: { text: string }) {
    const idea = this.write(id)
    idea.brief = { text: input.text, json: idea.brief?.json ?? null }
    return { ...idea }
  }
  async startPrototype(id: string, input?: { remarksText?: string }) {
    const idea = this.write(id)
    if (!idea.brief) fail('idea.no_brief')
    idea.status = 'prototyped'
    idea.prototype = {
      sha256: 'demo',
      bytes: DEMO_IDEA_HTML.length,
      screens: 2,
      generatedAt: new Date().toISOString(),
    }
    idea.remarksText = input?.remarksText ?? null
    idea.phase = {
      name: 'prototype',
      status: 'done',
      error: null,
      startedAt: new Date().toISOString(),
      finishedAt: new Date().toISOString(),
    }
    return { ...idea }
  }
  async getPrototypeHtml(id: string) {
    if (!this.read(id).prototype) fail('idea.no_prototype')
    return DEMO_IDEA_HTML
  }
  async getIdeaPrompt(id: string) {
    return this.read(id).brief?.text ?? fail('idea.no_brief')
  }
  async cancelIdea(id: string) {
    const idea = this.write(id)
    if (idea.phase)
      idea.phase = { ...idea.phase, status: 'failed', error: 'cancelled' }
    return { ...idea }
  }
  async deleteIdea(id: string) {
    this.write(id)
    this.ideas.delete(id)
    this.trees.delete(id)
  }
  async transferIdea(id: string, input: IdeaTransferInput) {
    const idea = this.read(id)
    if (idea.transfer) return idea.transfer
    if (!idea.prototype || !idea.brief) fail('idea.no_prototype')
    if (idea.sourceKind === 'repository' && !input.rightsAttested)
      fail('idea.rights_required')
    const dossier = await this.host.saveDossier({
      code: input.dossierCode,
      name: input.name,
      summary: input.summary,
      status: 'idea',
      moduleCode: null,
      ticketRefs: [],
    })
    const notes =
      idea.sourceKind === 'repository'
        ? `Source : ${idea.sourceUrl}@default — 1 fichiers lus le ${idea.updatedAt}\n\n${idea.ideaText}`
        : idea.ideaText
    const markdowns = [
      {
        fileName: 'prompt.md',
        text: idea.brief!.text,
        bytes: new Uint8Array(),
        role: 'prompt' as const,
      },
      {
        fileName: 'idea.md',
        text: notes,
        bytes: new Uint8Array(),
        role: 'notes' as const,
      },
    ]
    const imported = await this.host.createPrototypeImport(
      dossier.code,
      {
        title: input.name,
        toolName: 'geode-studio',
        toolDetail: 'demo',
        promptText: idea.brief!.text,
        imageCount: 0,
        htmlCount: 1,
        documents: markdowns.map(({ role }) => ({ role })),
        derivedFrom: [],
        derivation: input.derivation ?? 'none',
        attestationNoRealData: true,
        attestationRights: true,
        rightsTextVersion: PROTOTYPE_RIGHTS_TERMS_VERSION,
        supersedesImportId: null,
        clientWarnings: [],
      },
      {
        html: {
          text: DEMO_IDEA_HTML,
          fileName: 'prototype.html',
          fallback: false,
        },
        image: null,
        markdowns,
      },
    )
    idea.transfer = {
      dossierCode: dossier.code,
      dossierRef: dossier.code,
      importId: imported.id,
      sourceSummary:
        idea.sourceKind === 'repository' ? notes.split('\n')[0] : null,
    }
    idea.status = 'transferred'
    return idea.transfer
  }
}
